import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Search, Download, ChevronLeft, ChevronRight, Copy, Eye,
  UserPlus, Users, Wallet, Shield, AlertTriangle, CheckCircle,
  XCircle, Clock, Loader2, ArrowUpDown, Filter, Play,
  ToggleLeft, ToggleRight, CreditCard, MinusCircle, FileSpreadsheet,
} from 'lucide-react';

const VA_STATUSES = [
  { key: '', label: 'All' },
  { key: 'active', label: 'Active', color: 'emerald' },
  { key: 'not_created', label: 'Not Created', color: 'gray' },
];

const TIER_OPTIONS = [
  { key: '', label: 'All Tiers' },
  { key: '1', label: 'Tier 1' },
  { key: '2', label: 'Tier 2' },
  { key: '3', label: 'Tier 3' },
];

function StatusBadge({ status }) {
  const map = {
    active: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-400', label: 'Active' },
    pending: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400', label: 'Pending' },
    failed: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-400', label: 'Failed' },
    not_created: { bg: 'bg-gray-50', text: 'text-gray-500', dot: 'bg-gray-300', label: 'Not Created' },
    inactive: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-400', label: 'Inactive' },
  };
  const s = map[status] || map.not_created;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function TierBadge({ tier }) {
  const colors = { 1: 'bg-gray-100 text-gray-600', 2: 'bg-blue-50 text-blue-600', 3: 'bg-emerald-50 text-emerald-600' };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[tier] || colors[1]}`}>Tier {tier}</span>;
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
  toast.success('Copied to clipboard');
}

export default function StudentAccountsPage() {
  const { companyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [vaFilter, setVaFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState(new Set());
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [showConfirmBulk, setShowConfirmBulk] = useState(false);

  const loadStudents = useCallback(async () => {
    try {
      const data = await superAdminAPI.getStudents(companyId, {
        search, va_status: vaFilter, kyc_tier: tierFilter, sort_by: sortBy, sort_dir: sortDir, page, page_size: pageSize,
      });
      setStudents(data.students || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load students'); }
    finally { setLoading(false); }
  }, [companyId, search, vaFilter, tierFilter, sortBy, sortDir, page, pageSize]);

  const loadSummary = useCallback(async () => {
    try { setSummary(await superAdminAPI.getStudentsSummary(companyId)); } catch {}
  }, [companyId]);

  useEffect(() => { loadStudents(); loadSummary(); }, [loadStudents, loadSummary]);

  // Poll bulk progress
  useEffect(() => {
    if (!bulkProgress || bulkProgress.status !== 'running') return;
    const id = setInterval(async () => {
      try {
        const p = await superAdminAPI.getBulkVAProgress(companyId);
        setBulkProgress(p);
        if (p.status !== 'running') { loadStudents(); loadSummary(); clearInterval(id); }
      } catch {}
    }, 2000);
    return () => clearInterval(id);
  }, [bulkProgress, companyId, loadStudents, loadSummary]);

  const handleBulkCreate = async () => {
    setShowConfirmBulk(false);
    try {
      await superAdminAPI.triggerBulkVA(companyId);
      const p = await superAdminAPI.getBulkVAProgress(companyId);
      setBulkProgress(p);
      toast.success('Bulk VA creation started');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const openDetail = async (empId) => {
    setDetailLoading(true);
    try { setDetail(await superAdminAPI.getStudentDetail(empId)); }
    catch { toast.error('Failed to load student details'); }
    finally { setDetailLoading(false); }
  };

  const handleExport = async (exportAll = false) => {
    try {
      const resp = await superAdminAPI.exportStudents(companyId, {
        format: 'excel', search: exportAll ? '' : search, va_status: exportAll ? '' : vaFilter, export_all: exportAll,
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `Student_Accounts_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  const toggleSelect = (id) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => {
    if (selected.size === students.length) setSelected(new Set());
    else setSelected(new Set(students.map(s => s.id)));
  };

  const handleBulkActivate = async () => {
    try { await superAdminAPI.bulkActivate(companyId, { employee_ids: [...selected] }); toast.success('Activated'); loadStudents(); } catch { toast.error('Failed'); }
  };
  const handleBulkDeactivate = async () => {
    try { await superAdminAPI.bulkDeactivate(companyId, { employee_ids: [...selected], reason: 'Bulk deactivation' }); toast.success('Deactivated'); loadStudents(); } catch { toast.error('Failed'); }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-5" data-testid="student-accounts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900" data-testid="page-title">Employee Accounts</h1>
          <p className="text-sm text-gray-500 mt-0.5">{total} students</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport(false)} data-testid="export-filtered-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />Export{search || vaFilter ? ` Filtered (${total})` : ''}
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport(true)} data-testid="export-all-btn">
            <Download className="w-4 h-4 mr-1.5" />Export All
          </Button>
          <Button size="sm" className="bg-pink-500 hover:bg-pink-600 text-white" onClick={() => setShowConfirmBulk(true)} data-testid="bulk-create-btn">
            <UserPlus className="w-4 h-4 mr-1.5" />Create Virtual Accounts
          </Button>
        </div>
      </div>

      {/* Summary Strip */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="summary-strip">
          {[
            { label: 'Total Students', value: summary.total_students, icon: Users, color: 'blue' },
            { label: 'Active VAs', value: summary.active_accounts, icon: CheckCircle, color: 'emerald' },
            { label: 'Not Created', value: summary.not_created, icon: Clock, color: 'gray' },
            { label: 'Failed', value: summary.failed_creation, icon: XCircle, color: 'red' },
            { label: 'Total Balance', value: `NGN ${summary.total_wallet_balance?.toLocaleString()}`, icon: Wallet, color: 'emerald' },
            { label: 'Total Disbursed', value: `NGN ${summary.total_disbursed?.toLocaleString()}`, icon: CreditCard, color: 'blue' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label} className="border-gray-200">
              <CardContent className="p-3 flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-lg bg-${color}-50 flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 text-${color}-500`} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-sm font-bold text-gray-900">{value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Bulk Progress */}
      {bulkProgress && bulkProgress.status === 'running' && (
        <Card className="border-blue-200 bg-blue-50/30" data-testid="bulk-progress">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              <span className="text-sm font-bold text-blue-700">Creating Virtual Accounts...</span>
            </div>
            <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden mb-2">
              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${bulkProgress.percentage}%` }} />
            </div>
            <div className="flex justify-between text-xs text-blue-600">
              <span>{bulkProgress.created} created, {bulkProgress.failed} failed of {bulkProgress.total}</span>
              <span>{bulkProgress.percentage}% — ~{bulkProgress.estimated_remaining_seconds}s remaining</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Modal */}
      {showConfirmBulk && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="confirm-modal">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
                <h3 className="text-lg font-bold">Confirm Bulk Creation</h3>
              </div>
              <p className="text-sm text-gray-600">
                This will create production virtual accounts for all {summary?.not_created || '?'} students who don't already have one. This cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowConfirmBulk(false)}>Cancel</Button>
                <Button className="bg-pink-500 hover:bg-pink-600 text-white" onClick={handleBulkCreate} data-testid="confirm-bulk-btn">Confirm</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search name, email, account..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300"
            data-testid="search-input" />
        </div>
        <div className="flex gap-1.5">
          {VA_STATUSES.map(s => (
            <button key={s.key} onClick={() => { setVaFilter(s.key); setPage(0); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${vaFilter === s.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid={`filter-${s.key || 'all'}`}>{s.label}</button>
          ))}
        </div>
        <div className="flex gap-1.5">
          {TIER_OPTIONS.map(t => (
            <button key={t.key} onClick={() => { setTierFilter(t.key); setPage(0); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${tierFilter === t.key ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
              data-testid={`tier-filter-${t.key || 'all'}`}>{t.label}</button>
          ))}
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-gray-500">{selected.size} selected</span>
            <Button size="sm" variant="outline" onClick={handleBulkActivate} data-testid="bulk-activate-btn">
              <ToggleRight className="w-3.5 h-3.5 mr-1" />Activate
            </Button>
            <Button size="sm" variant="outline" onClick={handleBulkDeactivate} data-testid="bulk-deactivate-btn">
              <ToggleLeft className="w-3.5 h-3.5 mr-1" />Deactivate
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="students-table">
            <thead>
              <tr className="border-b bg-gray-50/50">
                <th className="p-3 w-10"><input type="checkbox" checked={selected.size === students.length && students.length > 0} onChange={toggleAll} /></th>
                <th className="p-3 text-left text-xs font-semibold text-gray-500 cursor-pointer" onClick={() => { setSortBy('name'); setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }}>
                  Student <ArrowUpDown className="inline w-3 h-3 ml-1" />
                </th>
                <th className="p-3 text-left text-xs font-semibold text-gray-500">Email</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-500">Phone</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-500">Virtual Account</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-500 cursor-pointer" onClick={() => { setSortBy('balance'); setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }}>
                  Balance <ArrowUpDown className="inline w-3 h-3 ml-1" />
                </th>
                <th className="p-3 text-left text-xs font-semibold text-gray-500">VA Status</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-500">KYC</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>
              ) : students.length === 0 ? (
                <tr><td colSpan={9} className="p-8 text-center text-gray-400">No students found</td></tr>
              ) : students.map(s => (
                <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition" data-testid={`student-row-${s.id}`}>
                  <td className="p-3"><input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)} /></td>
                  <td className="p-3">
                    <span className="font-medium text-gray-900">{s.first_name} {s.last_name}</span>
                  </td>
                  <td className="p-3 text-gray-500 text-xs">{s.email}</td>
                  <td className="p-3 text-gray-500 text-xs font-mono">{s.phone}</td>
                  <td className="p-3">
                    {s.virtual_account_number ? (
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs font-bold text-gray-900">{s.virtual_account_number}</span>
                        <button onClick={() => copyToClipboard(s.virtual_account_number)} className="text-gray-400 hover:text-gray-600">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    ) : <span className="text-xs text-gray-300">—</span>}
                  </td>
                  <td className="p-3 font-mono text-xs font-medium">NGN {s.wallet_balance?.toLocaleString()}</td>
                  <td className="p-3"><StatusBadge status={s.wallet_status === 'inactive' ? 'inactive' : s.va_status} /></td>
                  <td className="p-3"><TierBadge tier={s.kyc_tier} /></td>
                  <td className="p-3">
                    <button onClick={() => openDetail(s.id)} className="text-pink-500 hover:text-pink-700 text-xs font-medium" data-testid={`view-detail-${s.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
        </span>
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-3 py-1.5 text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Detail Slide-Over */}
      {(detail || detailLoading) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={(e) => { if (e.target === e.currentTarget) setDetail(null); }}>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-xl" data-testid="student-detail-panel">
            {detailLoading ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-pink-500" /></div>
            ) : detail ? (
              <StudentDetailPanel student={detail} onClose={() => setDetail(null)} onRefresh={() => { openDetail(detail.id); loadStudents(); loadSummary(); }} />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function StudentDetailPanel({ student, onClose, onRefresh }) {
  const [actionLoading, setActionLoading] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [debitAmount, setDebitAmount] = useState('');
  const [debitReason, setDebitReason] = useState('');
  const [showCreditForm, setShowCreditForm] = useState(false);
  const [showDebitForm, setShowDebitForm] = useState(false);

  const handleActivate = async () => {
    setActionLoading('activate');
    try { await superAdminAPI.activateStudent(student.id, { reason: 'Activated by admin' }); toast.success('Activated'); onRefresh(); }
    catch { toast.error('Failed'); } finally { setActionLoading(''); }
  };
  const handleDeactivate = async () => {
    setActionLoading('deactivate');
    try { await superAdminAPI.deactivateStudent(student.id, { reason: 'Deactivated by admin' }); toast.success('Deactivated'); onRefresh(); }
    catch { toast.error('Failed'); } finally { setActionLoading(''); }
  };
  const handleCreateVA = async () => {
    setActionLoading('createva');
    try { const r = await superAdminAPI.createStudentVA(student.id); toast.success(`VA created: ${r.account_number}`); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setActionLoading(''); }
  };
  const handleCredit = async () => {
    if (!creditAmount || !creditReason) { toast.error('Enter amount and reason'); return; }
    setActionLoading('credit');
    try {
      const r = await superAdminAPI.creditStudent(student.id, { amount: parseFloat(creditAmount), reason: creditReason });
      toast.success(`Credited NGN ${parseFloat(creditAmount).toLocaleString()}. New balance: NGN ${r.new_balance?.toLocaleString()}`);
      setCreditAmount(''); setCreditReason(''); setShowCreditForm(false); onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Credit failed'); }
    finally { setActionLoading(''); }
  };
  const handleDebit = async () => {
    if (!debitAmount || !debitReason) { toast.error('Enter amount and reason'); return; }
    setActionLoading('debit');
    try {
      const r = await superAdminAPI.debitStudent(student.id, { amount: parseFloat(debitAmount), reason: debitReason });
      toast.success(`Debited NGN ${parseFloat(debitAmount).toLocaleString()}. New balance: NGN ${r.new_balance?.toLocaleString()}`);
      setDebitAmount(''); setDebitReason(''); setShowDebitForm(false); onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Debit failed'); }
    finally { setActionLoading(''); }
  };

  const w = student.wallet || {};
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">{student.first_name} {student.last_name}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
      </div>

      {/* Account Details */}
      <Card className="border-gray-200">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase">Account Details</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-xs text-gray-400">Email</p><p className="font-medium">{student.email}</p></div>
            <div><p className="text-xs text-gray-400">Phone</p><p className="font-mono">{student.phone}</p></div>
          </div>
          {w.virtual_account_number ? (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-mono font-bold tracking-wider" data-testid="detail-va-number">{w.virtual_account_number}</span>
                <button onClick={() => copyToClipboard(w.virtual_account_number)} className="text-gray-400 hover:text-gray-600"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-sm text-gray-500">{w.virtual_account_name}</p>
              <p className="text-xs text-gray-400">Asset Microfinance Bank (090160)</p>
            </div>
          ) : (
            <Button size="sm" onClick={handleCreateVA} disabled={actionLoading === 'createva'} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="create-va-btn">
              {actionLoading === 'createva' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
              Create Virtual Account
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Wallet Details */}
      <Card className="border-gray-200">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase">Wallet</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-xs text-gray-400">Balance</p><p className="text-xl font-bold text-gray-900">NGN {w.balance?.toLocaleString()}</p></div>
            <div><p className="text-xs text-gray-400">Status</p><StatusBadge status={w.status} /></div>
            <div><p className="text-xs text-gray-400">Total Received</p><p className="font-medium">NGN {student.total_received?.toLocaleString()}</p></div>
            <div><p className="text-xs text-gray-400">Total Withdrawn</p><p className="font-medium">NGN {student.total_withdrawn?.toLocaleString()}</p></div>
          </div>
        </CardContent>
      </Card>

      {/* KYC & Compliance */}
      <Card className="border-gray-200">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase">KYC & Compliance</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-xs text-gray-400">BVN Status</p><p>{student.bvn_verified ? 'Verified' : 'Unverified'}</p></div>
            <div><p className="text-xs text-gray-400">KYC Tier</p><TierBadge tier={student.kyc_tier} /></div>
            <div><p className="text-xs text-gray-400">Single Txn Limit</p><p className="font-mono">NGN {student.single_transaction_limit?.toLocaleString()}</p></div>
            <div><p className="text-xs text-gray-400">Daily Limit</p><p className="font-mono">NGN {student.daily_transaction_limit?.toLocaleString()}</p></div>
            <div><p className="text-xs text-gray-400">Used Today</p><p className="font-mono">NGN {student.daily_used?.toLocaleString()}</p></div>
            <div><p className="text-xs text-gray-400">Remaining Today</p><p className="font-mono">NGN {student.daily_remaining?.toLocaleString()}</p></div>
          </div>
        </CardContent>
      </Card>

      {/* Admin Actions */}
      <Card className="border-gray-200">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase">Admin Actions</h3>
          <div className="flex flex-wrap gap-2">
            {w.status === 'active' ? (
              <Button size="sm" variant="outline" onClick={handleDeactivate} disabled={!!actionLoading} data-testid="deactivate-btn">
                <ToggleLeft className="w-3.5 h-3.5 mr-1" />Deactivate
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={handleActivate} disabled={!!actionLoading} data-testid="activate-btn">
                <ToggleRight className="w-3.5 h-3.5 mr-1" />Activate
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setShowCreditForm(!showCreditForm)} disabled={!!actionLoading} data-testid="show-credit-form-btn">
              <CreditCard className="w-3.5 h-3.5 mr-1" />Credit
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowDebitForm(!showDebitForm)} disabled={!!actionLoading} data-testid="show-debit-form-btn">
              <MinusCircle className="w-3.5 h-3.5 mr-1" />Debit
            </Button>
          </div>

          {/* Credit Form */}
          {showCreditForm && (
            <div className="p-3 bg-emerald-50 rounded-lg space-y-2 border border-emerald-200" data-testid="credit-form">
              <p className="text-xs font-semibold text-emerald-700">Manual Credit</p>
              <input type="number" step="0.01" min="0" value={creditAmount} onChange={e => setCreditAmount(e.target.value)}
                placeholder="Amount (NGN)" className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-300"
                data-testid="credit-amount-input" />
              <input type="text" value={creditReason} onChange={e => setCreditReason(e.target.value)}
                placeholder="Reason" className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-300"
                data-testid="credit-reason-input" />
              <div className="flex gap-2">
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={handleCredit}
                  disabled={actionLoading === 'credit'} data-testid="confirm-credit-btn">
                  {actionLoading === 'credit' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Confirm Credit
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowCreditForm(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Debit Form */}
          {showDebitForm && (
            <div className="p-3 bg-red-50 rounded-lg space-y-2 border border-red-200" data-testid="debit-form">
              <p className="text-xs font-semibold text-red-700">Manual Debit</p>
              <input type="number" step="0.01" min="0" value={debitAmount} onChange={e => setDebitAmount(e.target.value)}
                placeholder="Amount (NGN)" className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-300"
                data-testid="debit-amount-input" />
              <input type="text" value={debitReason} onChange={e => setDebitReason(e.target.value)}
                placeholder="Reason" className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-300"
                data-testid="debit-reason-input" />
              <div className="flex gap-2">
                <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={handleDebit}
                  disabled={actionLoading === 'debit'} data-testid="confirm-debit-btn">
                  {actionLoading === 'debit' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Confirm Debit
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowDebitForm(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card className="border-gray-200">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase">Transactions ({student.transactions?.length || 0})</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(student.transactions || []).length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No transactions</p>
            ) : student.transactions.map(t => (
              <div key={t.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                <div>
                  <span className={`font-medium ${t.type === 'inflow' ? 'text-emerald-600' : 'text-red-500'}`}>
                    {t.type === 'inflow' ? '+' : '-'}NGN {t.amount?.toLocaleString()}
                  </span>
                  <p className="text-gray-400 mt-0.5">{t.description?.slice(0, 50)}</p>
                </div>
                <div className="text-right">
                  <StatusBadge status={t.status === 'completed' ? 'active' : t.status} />
                  <p className="text-gray-400 mt-0.5">{t.created_at?.slice(0, 10)}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Audit Trail */}
      {student.audit_trail?.length > 0 && (
        <Card className="border-gray-200">
          <CardContent className="p-4 space-y-3">
            <h3 className="text-xs font-bold text-gray-500 uppercase">Audit Trail</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {student.audit_trail.map(a => (
                <div key={a.id} className="p-2 bg-gray-50 rounded text-xs">
                  <p className="font-medium text-gray-700">{a.action}</p>
                  <p className="text-gray-400">{a.description?.slice(0, 80)}</p>
                  <p className="text-gray-300 mt-0.5">{a.created_at?.slice(0, 16)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
